import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { ContextualModal, ContextualModalListItem, Link } from '@toqio/toqio-web-components';
import { PrimaryButton } from '../../../shared/components';
import { isCustomRangeValid } from '../dateRange';
import type { DateRangePreset } from '../types';
import { CalendarIcon } from './icons';
import { FIELD_BG, GREY, INK } from './styles';

/**
 * Date-range preset selector (PRD §2/§4a, MAPPINGS [Dropdown menu]). Uses the DS
 * `ContextualModal` + `ContextualModalListItem` (the lum-web `CalendarDateFilter`
 * pattern) so the trigger and the option list pick up the theme link colour and
 * selected state. Picking "Custom date" opens a separate modal with from/to
 * inputs (the contextual list can't host inline inputs — it closes on click),
 * mirroring lum-web's `CustomDateFormModal`; Apply is blocked while
 * `toDate < fromDate`.
 */
const PRESETS: DateRangePreset[] = [
  'current-month',
  'last-month',
  'current-year',
  'last-year',
  'custom',
];

const presetLabel = (preset: DateRangePreset): string => {
  switch (preset) {
    case 'current-month':
      return t`Current month`;
    case 'last-month':
      return t`Last month`;
    case 'current-year':
      return t`Current year`;
    case 'last-year':
      return t`Last year`;
    case 'custom':
      return t`Custom date`;
  }
};

/** Trigger content: calendar glyph + label, inheriting the DS Link colour. */
const TriggerInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
`;

const Card = styled.div`
  position: relative;
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-radius: 24px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font:
    700 24px/32px Inter,
    sans-serif;
  color: ${INK};
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const DateField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font:
    400 14px/20px Inter,
    sans-serif;
  color: ${GREY};

  input {
    font:
      400 16px/24px Inter,
      sans-serif;
    color: ${INK};
    background: ${FIELD_BG};
    border: none;
    border-radius: 8px;
    padding: 8px 12px;
  }
`;

const Invalid = styled.p`
  margin: 0;
  font:
    400 14px/20px Inter,
    sans-serif;
  color: #f44336;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export type DateRangeDropdownProps = {
  value: DateRangePreset;
  customFrom?: string;
  customTo?: string;
  onChange: (preset: DateRangePreset, custom?: { fromDate?: string; toDate?: string }) => void;
};

export const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  value,
  customFrom,
  customTo,
  onChange,
}) => {
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState(customFrom ?? '');
  const [to, setTo] = useState(customTo ?? '');

  // Re-seed the draft whenever the custom-date modal (re)opens.
  useEffect(() => {
    if (!customOpen) return;
    setFrom(customFrom ?? '');
    setTo(customTo ?? '');
  }, [customOpen, customFrom, customTo]);

  useEffect(() => {
    if (!customOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCustomOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [customOpen]);

  const pick = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setCustomOpen(true);
      return;
    }
    onChange(preset);
  };

  const rangeValid = isCustomRangeValid(from, to);

  const applyCustom = () => {
    if (!rangeValid) return;
    setCustomOpen(false);
    onChange('custom', { fromDate: from, toDate: to });
  };

  return (
    <>
      <ContextualModal
        buttonElement={
          <Link aria-haspopup="listbox">
            <TriggerInner>
              <CalendarIcon />
              {presetLabel(value)}
            </TriggerInner>
          </Link>
        }
        contentElement={PRESETS.map((preset) => (
          <ContextualModalListItem
            key={preset}
            label={presetLabel(preset)}
            selected={preset === value}
            onClick={() => pick(preset)}
          />
        ))}
      />

      {customOpen && (
        <Overlay
          onMouseDown={() => setCustomOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t`Custom date`}
        >
          <Card onMouseDown={(e) => e.stopPropagation()}>
            <Title>
              <Trans>Custom date</Trans>
            </Title>
            <Fields>
              <DateField>
                <Trans>From</Trans>
                <input
                  type="date"
                  value={from}
                  max={to || undefined}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </DateField>
              <DateField>
                <Trans>To</Trans>
                <input
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                />
              </DateField>
            </Fields>
            {!rangeValid && (
              <Invalid>
                <Trans>The end date must be after the start date.</Trans>
              </Invalid>
            )}
            <Footer>
              <PrimaryButton disabled={!rangeValid} onClick={applyCustom}>
                <Trans>Apply</Trans>
              </PrimaryButton>
            </Footer>
          </Card>
        </Overlay>
      )}
    </>
  );
};
